import React from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import "./SUHRights.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

/* ---- Simple inline icons (no external icon-font dependency) ---- */
const IconGroup = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <circle cx="9" cy="8" r="3" />
    <path d="M2.5 20c0-3.5 2.9-6 6.5-6s6.5 2.5 6.5 6" />
    <circle cx="17.5" cy="8.5" r="2.3" />
    <path d="M15.7 14c2.6.4 4.7 2.5 4.7 5.5" />
  </svg>
);

const IconHouse = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M4 11l8-7 8 7" />
    <path d="M6 10v10h12V10" />
    <path d="M10 20v-6h4v6" />
  </svg>
);

const IconCounsel = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <circle cx="8" cy="8" r="3" />
    <path d="M2.5 19c0-3 2.5-5.3 5.5-5.3s5.5 2.3 5.5 5.3" />
    <path d="M15 6a3 3 0 010 6" />
    <path d="M15 13c2.5.4 4.5 2.2 4.5 4.6" />
  </svg>
);

const IconMegaphone = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M3 10v4h3l6 4V6l-6 4H3z" />
    <path d="M14 9a4 4 0 010 6" />
    <path d="M17 6a8 8 0 010 12" />
  </svg>
);

const IconScale = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M12 3v18" />
    <path d="M5 8h14" />
    <path d="M5 8l-3 6a3 3 0 006 0l-3-6z" />
    <path d="M19 8l-3 6a3 3 0 006 0l-3-6z" />
    <path d="M8 21h8" />
  </svg>
);

const IconHeartFilled = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" stroke="none">
    <path d="M12 21s-7.5-4.6-10-9.3C.4 8.6 2 5 5.6 5c2 0 3.4 1.1 4.4 2.6C11 6.1 12.4 5 14.4 5 18 5 19.6 8.6 22 11.7 19.5 16.4 12 21 12 21z" />
  </svg>
);

const IconUserPlus = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="9" cy="8" r="3.2" />
    <path d="M2.5 20c0-3.6 2.9-6.2 6.5-6.2s6.5 2.6 6.5 6.2" />
    <path d="M18 8v5M20.5 10.5h-5" />
  </svg>
);

const IconArrowLeft = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#1f9d55" strokeWidth="2">
    <path d="M19 12H5" />
    <path d="M11 6l-6 6 6 6" />
  </svg>
);

const IconArrowRight = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#1f9d55" strokeWidth="2">
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);

// Renamed component
const SUHRights = () => {
  // Data for the initiatives section for cleaner code
  const initiatives = [
    {
      icon: <IconHouse />,
      text: "Providing Shelter, Healthcare, and Legal Aid",
    },
    {
      icon: <IconCounsel />,
      text: "Offering Counseling and Livelihood Opportunities",
    },
    {
      icon: <IconGroup />,
      text: "Collaborating with Local Bodies to Address Discrimination",
    },
    {
      icon: <IconMegaphone />,
      text: "Running Advocacy and Awareness Campaigns",
    },
    {
      icon: <IconScale />,
      text: "Supporting the Path to Justice and Self-Reliance",
    },
  ];

  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* Hero Section */}
      <section className="page-header page-header--suh">
        <div
          className="page-header__bg"
          style={{
            backgroundImage: "url(/assets/images/sections/SUH.jpg)", // Placeholder, update with a relevant image
          }}
        ></div>
        <div className="shape1 float-bob-x">
          <img src="assets/images/shapes/page-header-shape1.png" alt="" />
        </div>
        <div className="shape2">
          <img src="assets/images/shapes/page-header-shape2.png" alt="" />
        </div>
        <Container>
          <div className="page-header__inner text-center">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Upholding Rights of Equality for SUH Persons
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{ color: "#dad2d0", fontSize: "22px", marginTop: "20px" }}
            >
              Working to uphold the rights, dignity, and inclusion of Sexual
              and Unhoused persons since 2016.
            </motion.p>
          </div>
        </Container>
      </section>

      {/* Introduction Section */}
      <section className="py-5 suh-journey-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-center justify-content-center g-5">
              <motion.div className="col-lg-6" variants={staggerItem}>
                <div className="suh-heading-row">
                  <span className="suh-heading-icon">
                    <IconGroup />
                  </span>
                  <h2 className="fw-bold" style={{ color: "#006633" }}>
                    A Journey Rooted in Equality
                  </h2>
                </div>
                <p className="lh-lg">
                  Since 2016, Vikash has been working with Sexual and Unhoused
                  (SUH) persons to ensure their safety, respect, and access
                  to essential services. Through continuous advocacy, we
                  strive to change public perceptions and promote acceptance,
                  recognizing their humanity and supporting their path
                  toward justice and self-reliance.
                </p>
                <div className="suh-heading-underline"></div>
              </motion.div>
              <motion.div className="col-lg-6" variants={staggerItem}>
                <div className="suh-image-card">
                  <img
                    src="/assets/images/resources/action2.jpg"
                    alt="Community support for SUH persons"
                    className="img-fluid rounded-5 shadow"
                  />
                  <span className="suh-image-accent"></span>
                </div>
              </motion.div>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Key Initiatives Section */}
      <section className="py-5 suh-interventions-section">
        <Container>
          <div className="suh-interventions-box">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeIn}
              className="text-center mb-5"
            >
              <h2 className="fw-bold suh-interventions-title">
                <IconArrowRight /> Our Core Interventions <IconArrowLeft />
              </h2>
              <p className="lead suh-subtext">
                We focus on providing comprehensive support to ensure SUH
                individuals can live freely and participate fully in
                society.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={staggerContainer}
            >
              <Row className="g-4 justify-content-center">
                {initiatives.map((initiative, index) => (
                  <Col md={6} lg={4} key={index}>
                    <motion.div variants={staggerItem} className="h-100">
                      <Card className="p-3 border-0 shadow-sm h-100 suh-initiative-card">
                        <Card.Body className="d-flex align-items-center gap-3 p-0">
                          <span className="suh-initiative-icon">
                            {initiative.icon}
                          </span>
                          <p className="mb-0">{initiative.text}</p>
                        </Card.Body>
                      </Card>
                    </motion.div>
                  </Col>
                ))}
              </Row>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Call To Action Section */}
      <section className="py-5 suh-cta-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <div className="suh-cta-box">
              <div className="suh-cta-photo">
                <img
                  src="/assets/images/resources/heart-hands.jpg"
                  alt="Hands holding a heart"
                />
              </div>
              <div className="suh-cta-content">
                <h2 className="fw-bold mb-2" style={{ color: "#16341f" }}>
                  Support Dignity and Equality
                </h2>
                <p className="mb-4">
                  Your contribution helps provide shelter, healthcare, and
                  legal aid to vulnerable individuals.
                </p>
                <div className="suh-cta-buttons">
                  <Button className="suh-btn suh-btn--solid" onClick={openDonation}>
                    <IconHeartFilled /> Donate
                  </Button>
                  <Button
                    className="suh-btn suh-btn--outline"
                    onClick={() => navigate("/interns")}
                  >
                    <IconUserPlus /> Join Us
                  </Button>
                </div>
              </div>
              <div className="suh-cta-deco"></div>
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default SUHRights;